import { Banner } from '@ticketmaster/aurora';
import TmdsClient from '@ticketmaster/tm1pos-tmds-client';
import IconAlert from '@ticketmaster/tm1pos-web-shared/components/icons/IconAlert24';
import { EMAIL, SMS } from '@ticketmaster/tm1pos-web-shared/constants';
import { selectFeatureFlags } from '@ticketmaster/tm1pos-web-shared/feature-flags';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { change, reduxForm } from 'redux-form/immutable';
import { useCheckoutContext } from '../../../../../context/checkout-provider';
import { useAppSelector } from '../../../../store/hooks';
import emvmessages from '../../../App/messages';
import messages from '../../messages';
import './checkout-form.scss';
import { selectCheckoutFormState } from './checkout-form-selector';
import { isCheckoutFormComplete } from './checkout-utils';
import DeliveryForm from './components/delivery-form/delivery-form';
import { DeliveryTypeControls } from './components/delivery-type-controls/delivery-type-controls';
import GlobalAccount from './components/global-account/global-account-connected';
import { PaymentTypeControls } from './components/payment-type-controls/payment-type-controls';
import { PrintReceipt } from './components/print-receipt/print-receipt';
import { SubmitButton } from './components/submit-button/submit-button';
import { CARD, CASH, CHECKOUT_FORM_TITLE, OTHER } from './constants';
import { PaymentForm } from './containers/payment-form/payment-form';
import { handleTmdsPrinterChange } from './handle-tmds-printer-change';
import { selectFormValues, selectPrintReceiptData } from './selectors';
import type { InjectedIntlProps } from 'react-intl';
import type { FormProps } from 'redux-form';

export type FieldNames = {
  cardHolderName: 'cardHolderName';
  accountNumber: 'accountNumber';
  expDate: 'expDate';
  creditCardTypes: 'creditCardTypes';
  manualCardEntry: 'manualCardEntry';
  authCode: 'authCode';
  paymentMethod: 'paymentMethod';
  otherPaymentMethod: 'otherPaymentMethod';
  paymentInfo: 'paymentInfo';
  cashTendered: 'cashTendered';
  changeDue: 'changeDue';
};

type CheckoutFormProps = InjectedIntlProps & {
  invalid?: boolean; // this prop is passed from redux-form HOC
  handleSubmit?: () => void;
};

const CheckoutFormBase = ({ intl, handleSubmit, invalid }: CheckoutFormProps) => {
  const dispatch = useDispatch();
  const {
    paymentMethod,
    deliveryMethod,
    manualCardEntry,
    cartEmpty,
    checkoutInProgress,
    printReceiptData,
    currentEvent,
    cartTotal,
    isEmvSupported,
    emvCheckoutPageError,
    useEmailAsDelivery,
    shouldRequireFullName,
    linkGlobalAccount,
    fullName,
    cardHolderName,
    orderIdToRetry,
    phonePre,
    hasEMVError,
    hasActivePrinter,
    hasPrintReceipt,
    hasActivePaymentDevice,
    cardPaymentEnabled,
    linkGlobalAccountEnabled,
  } = useSelector(selectCheckoutFormState);

  const featureFlags = useAppSelector(selectFeatureFlags);
  const checkoutContext = useCheckoutContext();

  const respectRotEntryToken = paymentMethod === CASH || (paymentMethod === CARD && manualCardEntry);
  const errRetryMsg = useRef<HTMLDivElement | null>(null);
  const [prevCardHolderName, setPrevCardHolderName] = useState<string | null>(null);

  const formLabels = useMemo(() => {
    const isFullNameRequired =
      (respectRotEntryToken && currentEvent.rotatingEntryTokenEnabled) ||
      (isEmvSupported && paymentMethod === CARD && (manualCardEntry || shouldRequireFullName));
    const isCardHolderNameRequired =
      (respectRotEntryToken && currentEvent.rotatingEntryTokenEnabled) || (paymentMethod === CARD && manualCardEntry);
    const isEmailRequired = deliveryMethod === EMAIL || linkGlobalAccount;

    const fullNamePlaceholder = `${intl.formatMessage(messages.fullNamePlaceholder)} ${
      isFullNameRequired ? intl.formatMessage(messages.requiredTag) : ''
    }`;
    const phoneNumberPlaceholder = `${intl.formatMessage(messages.phoneNumberPlaceholder)} ${
      deliveryMethod === SMS ? intl.formatMessage(messages.requiredTag) : ''
    }`;
    const emailPlaceholder = `${intl.formatMessage(messages.emailPlaceholder)} ${
      isEmailRequired ? intl.formatMessage(messages.requiredTag) : ''
    }`;
    const notePlaceholder = intl.formatMessage(messages.notePlaceholder);
    const authCodePlaceholder = intl.formatMessage(messages.authCode);
    const printReceiptToggleLabel = intl.formatMessage(messages.printReceipts);
    const cardFormLabels = {
      cardHolderLabel: `${intl.formatMessage(messages.cardHolderNamePlaceholder)} ${
        isCardHolderNameRequired ? intl.formatMessage(messages.requiredTag) : ''
      }`,
      cardNumberLabel: intl.formatMessage(messages.cardNumberPlaceholder),
      cardExpDateLabel: intl.formatMessage(messages.cardExpDatePlaceholder),
    };
    const otherFormLabels = {
      paymentInfoLabel: intl.formatMessage(messages.paymentInfoPlaceholder), //  cardNumberPlaceholder
      otherPaymentsLabel: `${intl.formatMessage(
        messages.otherPaymentsPlaceholder,
      )} ${intl.formatMessage(messages.requiredTag)}`,
    };

    return {
      fullNamePlaceholder,
      phoneNumberPlaceholder,
      emailPlaceholder,
      notePlaceholder,
      authCodePlaceholder,
      printReceiptToggleLabel,
      cardFormLabels,
      otherFormLabels,
    };
  }, [
    intl,
    currentEvent,
    deliveryMethod,
    paymentMethod,
    manualCardEntry,
    shouldRequireFullName,
    respectRotEntryToken,
    isEmvSupported,
    linkGlobalAccount,
  ]);

  // This is a synchronization between the cardHolderName and fullName fields.
  // This is necessary to ensure that the fullName field is always up to date with the cardHolderName field.
  useEffect(() => {
    if (!fullName || fullName === prevCardHolderName) {
      dispatch(change(CHECKOUT_FORM_TITLE, 'fullName', cardHolderName));
    }

    setPrevCardHolderName(cardHolderName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardHolderName, dispatch]);

  useEffect(() => {
    const defaultPaymentMethod = cartTotal.amount === 0 ? OTHER : CARD;
    dispatch(change(CHECKOUT_FORM_TITLE, 'paymentMethod', defaultPaymentMethod));
  }, [cartTotal, dispatch]);

  useEffect(() => {
    if (errRetryMsg?.current && orderIdToRetry && !checkoutInProgress) {
      errRetryMsg.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  }, [orderIdToRetry, checkoutInProgress, errRetryMsg]);

  // NOTE: Any changes here should be duplicated in `FieldNames` type.
  // Unfortunately, extracting this object and placing it above the component
  // causes odd side effects in other forms. The duplication here and in the type
  // is necessary to avoid regressions at the moment.
  const fieldNames: FieldNames = {
    cardHolderName: 'cardHolderName',
    accountNumber: 'accountNumber',
    expDate: 'expDate',
    creditCardTypes: 'creditCardTypes',
    manualCardEntry: 'manualCardEntry',
    authCode: 'authCode',
    paymentMethod: 'paymentMethod',
    otherPaymentMethod: 'otherPaymentMethod',
    paymentInfo: 'paymentInfo',
    cashTendered: 'cashTendered',
    changeDue: 'changeDue',
  };

  return (
    <form className="sdr-checkout__form" onSubmit={handleSubmit} autoComplete="off">
      <div className="sdr-checkout__form-inner-wrap">
        {orderIdToRetry ? (
          <div className="sdr-checkout__payment-fail-wrap" ref={errRetryMsg}>
            <div className="sdr-checkout__payment-fail">
              <IconAlert />
              <FormattedMessage {...messages.checkoutPaymentErrMsg} values={{ orderId: orderIdToRetry }} />
            </div>
          </div>
        ) : null}
        {hasEMVError && emvCheckoutPageError ? (
          <div className="sdr-checkout__payment-emv-fail">
            <Banner
              heading={intl.formatMessage(emvCheckoutPageError.message)}
              isOpen
              variant={emvCheckoutPageError.isWarning ? 'alert' : 'error'}
              {...(emvCheckoutPageError.terminalMessage
                ? {
                    content: intl.formatMessage(emvmessages.terminalMsg, {
                      terminalMessage: emvCheckoutPageError.terminalMessage,
                    }),
                    isExpanded: true,
                  }
                : {})}
            />
          </div>
        ) : null}
        <div className="sdr-checkout__payment-title-wrap">
          <div className="sdr-checkout__section-title">
            <FormattedMessage {...messages.payment} />
          </div>
        </div>
        {[
          <PaymentTypeControls
            fieldName={fieldNames.paymentMethod}
            paymentMethod={paymentMethod}
            key={paymentMethod}
          />,
          <PaymentForm
            emvSupported={isEmvSupported}
            manualCardEntry={manualCardEntry}
            paymentMethod={paymentMethod}
            authCodePlaceholder={formLabels.authCodePlaceholder}
            cardFormLabels={formLabels.cardFormLabels}
            otherFormLabels={formLabels.otherFormLabels}
            fieldNames={fieldNames}
            cashToTender={cartTotal}
            cashFormAutoSelect
            key="payment-form"
          />,
        ]}
        <DeliveryTypeControls emailAsDelivery={useEmailAsDelivery} />
        <PrintReceipt
          deliveryMethod={deliveryMethod}
          label={formLabels.printReceiptToggleLabel}
          active={printReceiptData.active}
          quantity={printReceiptData.quantity}
        />
        {linkGlobalAccountEnabled && <GlobalAccount />}
        <DeliveryForm
          deliveryMethod={deliveryMethod}
          currentEvent={currentEvent}
          respectRotEntryToken={respectRotEntryToken}
          {...formLabels}
          phonePre={phonePre}
          isManualEntry={manualCardEntry}
          paymentMethod={paymentMethod}
        />
      </div>
      <SubmitButton
        isOrderUnpaid={!!orderIdToRetry}
        isPaymentDeviceConnected={isEmvSupported}
        checkoutInProgress={checkoutInProgress}
        invalid={
          featureFlags?.designSystemCheckoutForm
            ? !checkoutContext.isFormValid ||
              cartEmpty ||
              !isCheckoutFormComplete(
                isEmvSupported,
                manualCardEntry,
                !invalid,
                fullName,
                deliveryMethod,
                hasPrintReceipt,
                hasActivePrinter,
                hasActivePaymentDevice,
                paymentMethod,
                cardPaymentEnabled,
              )
            : cartEmpty ||
              !isCheckoutFormComplete(
                isEmvSupported,
                manualCardEntry,
                !invalid,
                fullName,
                deliveryMethod,
                hasPrintReceipt,
                hasActivePrinter,
                hasActivePaymentDevice,
                paymentMethod,
                cardPaymentEnabled,
              )
        }
      />
    </form>
  );
};

export const CheckoutForm = injectIntl(CheckoutFormBase);

const ConnectedReduxForm = reduxForm({
  form: CHECKOUT_FORM_TITLE,
  validate: () => ({}),
  onChange: (values, _dispatch, _props, previousValues) => {
    // @ts-expect-error values is not typed
    handleTmdsPrinterChange(values, previousValues);
  },
})(CheckoutForm as any);

type ConnectedReduxFormProps = Partial<FormProps<object, object, string>>;

export const ConnectedReduxFormInitial = (props: ConnectedReduxFormProps) => {
  const formValues = useSelector(selectFormValues);
  const printReceiptCount = useSelector(selectPrintReceiptData);
  const initialValues = {
    ...formValues,
    printer: TmdsClient.selectedPrinterData,
    printReceiptCount,
  };

  return <ConnectedReduxForm initialValues={initialValues} {...props} />;
};

export default ConnectedReduxFormInitial;
